import { type PointerEvent, useRef } from "react";
import { Mic, MicOff } from "lucide-react";
import { cn } from "../lib/utils.ts";
import type { MicMode } from "./MicModeSelector.tsx";

interface Props {
  listening: boolean;
  mode: MicMode;
  onToggle: () => void;
  onPress: () => void;
  onRelease: () => void;
  disabled?: boolean;
}

export function MicButton({
  listening,
  mode,
  onToggle,
  onPress,
  onRelease,
  disabled,
}: Props) {
  const pressedRef = useRef(false);

  const handlePress = (e: PointerEvent<HTMLButtonElement>) => {
    if (mode !== "ptt" || disabled) return;
    if (pressedRef.current) return;
    pressedRef.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    onPress();
  };

  const handleRelease = () => {
    if (mode !== "ptt") return;
    if (!pressedRef.current) return;
    pressedRef.current = false;
    onRelease();
  };

  const handleClick = () => {
    if (mode !== "handsFree" || disabled) return;
    onToggle();
  };

  const ariaLabel = mode === "ptt"
    ? "Hold to speak"
    : listening
    ? "Stop recording"
    : "Start recording";

  return (
    <button
      onClick={handleClick}
      onPointerDown={handlePress}
      onPointerUp={handleRelease}
      onPointerCancel={handleRelease}
      onLostPointerCapture={handleRelease}
      onContextMenu={(e) => {
        if (mode === "ptt") e.preventDefault();
      }}
      disabled={disabled}
      aria-label={ariaLabel}
      aria-pressed={mode === "ptt" ? listening : undefined}
      className={cn(
        "relative h-36 w-36 md:h-44 md:w-44 lg:h-48 lg:w-48 xl:h-56 xl:w-56 rounded-full grid place-items-center transition-transform active:scale-95 focus-visible:outline-hidden focus-visible:ring-8 focus-visible:ring-white/30 disabled:opacity-50 select-none touch-none",
        listening
          ? "bg-gradient-to-br from-emerald-300 to-emerald-500"
          : "bg-gradient-to-br from-rose-400 to-rose-600",
      )}
    >
      {listening && (
        <>
          <span className="absolute inset-0 rounded-full bg-emerald-300/60 animate-ping-soft" />
          <span
            className="absolute inset-0 rounded-full bg-emerald-300/40 animate-ping-soft"
            style={{ animationDelay: "0.6s" }}
          />
        </>
      )}
      <span className="relative z-10 grid place-items-center h-24 w-24 md:h-32 md:w-32 lg:h-36 lg:w-36 xl:h-40 xl:w-40 rounded-full bg-white/15 backdrop-blur-md shadow-inner">
        {listening
          ? (
            <Mic
              className="h-12 w-12 md:h-16 md:w-16 xl:h-20 xl:w-20 text-white"
              strokeWidth={2.5}
            />
          )
          : (
            <MicOff
              className="h-12 w-12 md:h-16 md:w-16 xl:h-20 xl:w-20 text-white"
              strokeWidth={2.5}
            />
          )}
      </span>
    </button>
  );
}
